import {
  Area,
  AreaChart as RechartsAreaChart,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ChartFrame } from '../../molecules/ChartFrame/ChartFrame';
import type { CartesianChartProps } from '../../molecules/ChartFrame/chartProps';
import { chartAriaLabel, isEmpty, seriesPayload } from '../../molecules/ChartFrame/chartProps';
import { AnchoredTooltipSurface } from '../../molecules/ChartFrame/AnchoredTooltipSurface';
import { useAnchoredTooltip } from '../../molecules/ChartFrame/useAnchoredTooltip';
import { chartAnimation, chartMarkGeometry, gridProps, legendItems, seriesColor, trendLineProps, type TrendSeries, tooltipCursor, xAxisProps, yAxisProps } from '../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../molecules/ChartLegend/ChartLegend';
import { ChartTooltip } from '../../molecules/ChartTooltip/ChartTooltip';

/**
 * An area chart, drawn with the Figma chart chrome.
 *
 * Three stacking modes, because an area means something different in each:
 *
 * - **`stack`** — the default. Bands sit on one another and the top edge is the
 *   total, which is what an area chart is usually asked for.
 * - **`share`** — Recharts' `expand` offset. Each band is its share of that
 *   period's total and the axis runs 0–100%. The composition is the subject and
 *   the total is deliberately unreadable, which is the trade: a series can grow
 *   in absolute terms while its band shrinks.
 * - **`none`** — bands from a common baseline, overlapping. Honest for two
 *   series, unreadable for four; nothing in the API stops you, but the fill
 *   opacity is what makes it survivable at all.
 *
 * The legend sits below the plot as DOM, in `ChartFrame`'s own slot, rather than
 * inside the SVG — the same arrangement every other chart here uses, and the
 * reason its type comes from CSS.
 *
 * **Not in Figma.** The file draws no area; the fill opacity and the stacking
 * are derived, the chrome and the palette are the file's. See plan.md §7 item
 * 132.
 *
 * ```tsx
 * <AreaChart data={rows} xKey="date" series={series} stack="share" />
 * ```
 */
export type AreaChartProps = Omit<CartesianChartProps, 'series'> & {
  /**
   * `TrendSeries`, so a band can be drawn dashed as a prior period or muted
   * behind another. Optional on every series and `ChartSeries` is assignable,
   * so existing callers render unchanged. Widened here rather than on
   * `CartesianChartProps` so `BarChart` does not inherit a prop it cannot draw.
   */
  series: TrendSeries[];
  /** How the bands relate to one another. */
  stack?: 'stack' | 'share' | 'none';
  /** Smoothed by default — an area reads as a continuous quantity. */
  curve?: 'linear' | 'monotone';
};

/** Shares are fractions of one, so the axis that reads them is a percentage. */
const percent = (value: number) =>
  value.toLocaleString('en-US', { style: 'percent', maximumFractionDigits: 0 });

export function AreaChart({
  data,
  xKey,
  series,
  height,
  legend = 'bottom',
  tooltip = true,
  loading = false,
  empty,
  emptyLabel,
  formatValue,
  formatDetail,
  xAxisTitle,
  yAxisTitle,
  label,
  stack = 'stack',
  curve = 'monotone',
  onViewDetails,
  viewDetailsLabel,
  className,
}: AreaChartProps) {
  const items = legendItems(series);
  const isShare = stack === 'share';

  const anchor = useAnchoredTooltip({ count: data.length, enabled: tooltip, onDrill: onViewDetails });
  const anchored = anchor.point ? data[anchor.point.index] : undefined;

  return (
    <ChartFrame
      height={height}
      xAxisTitle={xAxisTitle}
      yAxisTitle={yAxisTitle}
      loading={loading}
      empty={empty ?? isEmpty(data, series)}
      emptyLabel={emptyLabel}
      label={label ?? chartAriaLabel(isShare ? 'Share area chart' : 'Area chart', series, xKey)}
      legend={legend === 'none' ? undefined : <ChartLegend items={items} />}
      legendPlacement="bottom"
      plotRef={anchor.plotRef}
      plotProps={anchor.plotProps}
      hover={
        anchored && (
          <AnchoredTooltipSurface anchor={anchor}>
            <ChartTooltip
              active
              label={anchored[xKey] ?? undefined}
              payload={seriesPayload(anchored, series)}
              /*
               * On a share plot the band *is* a proportion, so the readout leads
               * with it — and keeps the caller's own figure beside it, because
               * the count is what the share was computed from and the one a
               * reader can act on. The payload carries the raw value either way;
               * the division happens in `ChartTooltip`, where the point's total
               * is known.
               */
              share={isShare}
              formatter={formatValue && ((v) => formatValue(Number(v)))}
              detail={
                formatDetail &&
                ((entry) =>
                  entry.payload ? formatDetail(entry.payload, String(entry.dataKey)) : undefined)
              }
              action={anchor.drill}
              actionLabel={viewDetailsLabel}
              locked={anchor.locked}
            />
          </AnchoredTooltipSurface>
        )
      }
      className={className}
    >
      <RechartsAreaChart
        data={data}
        stackOffset={isShare ? 'expand' : undefined}
        {...anchor.chartProps}
      >
        <CartesianGrid {...gridProps()} />
        <XAxis {...xAxisProps(xKey, data.length)} />
        {/*
         * A share axis is fixed to 0–100%: letting it auto-scale would crop the
         * one thing the reader is being asked to judge, which is how much of the
         * whole each band holds.
         */}
        <YAxis
          {...yAxisProps()}
          domain={isShare ? [0, 1] : undefined}
          tickFormatter={isShare ? percent : formatValue}
        />
        {tooltip && (
          /* Cursor only — see `LineChart`, where this arrangement is explained. */
          <Tooltip
            {...anchor.cursorProps}
            isAnimationActive={false}
            content={() => null}
            cursor={tooltipCursor('line')}
          />
        )}
        {series.map((s, i) => {
          const color = seriesColor(i, s.color);
          return (
            <Area
              key={s.key}
              type={curve}
              dataKey={s.key}
              name={s.label}
              stackId={stack === 'none' ? undefined : 'stack'}
              /*
               * Stroke, weight, dash and opacity from `trendLineProps` — it
               * resolves the same `lineStrokeWidth` this used directly, so a
               * plain band is unchanged. The fill stays local: it is the area's
               * own affordance, not part of a line's identity.
               */
              {...trendLineProps(s, i)}
              fill={color}
              fillOpacity={chartMarkGeometry.areaFillOpacity}
              activeDot={{ r: chartMarkGeometry.activeDotRadius, strokeWidth: 0 }}
              {...chartAnimation}
            />
          );
        })}
      </RechartsAreaChart>
    </ChartFrame>
  );
}
